import { Link } from 'react-router-dom';
import { describeActivity, actorIdOf } from '../utils/activityDescription';

const METHOD_CHIP = {
    POST:   'bg-emerald-50 text-emerald-700',
    PATCH:  'bg-blue-50 text-blue-700',
    PUT:    'bg-blue-50 text-blue-700',
    DELETE: 'bg-rose-50 text-rose-700',
    GET:    'bg-ink-100 text-ink-700',
};

const STATUS_CHIP = (s) => {
    if (!s) return 'bg-ink-100 text-ink-700';
    if (s >= 500) return 'bg-rose-100 text-rose-700';
    if (s >= 400) return 'bg-amber-100 text-amber-700';
    if (s >= 200) return 'bg-emerald-100 text-emerald-700';
    return 'bg-ink-100 text-ink-700';
};

/**
 * Shared activity log table — human-readable "What happened" plus optional actor links.
 */
export default function ActivityLogTable({ items, showActor = true, emptyMessage = 'No activity matches these filters.' }) {
    return (
        <div className="ag-card p-0 overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="bg-ink-100 sticky top-0">
                    <tr>
                        <th className="px-3 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-ink-500 whitespace-nowrap">When</th>
                        {showActor && (
                            <th className="px-3 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-ink-500 whitespace-nowrap">Actor</th>
                        )}
                        <th className="px-3 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-ink-500">What happened</th>
                        <th className="px-3 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-ink-500">Method</th>
                        <th className="px-3 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-ink-500">Status</th>
                        <th className="px-3 py-3 text-left text-[10px] font-bold uppercase tracking-wider text-ink-500">IP</th>
                    </tr>
                </thead>
                <tbody>
                    {items.map(l => {
                        const { summary, detail } = describeActivity(l);
                        const actorId = actorIdOf(l);
                        const actorName = l.actorName || l.actor?.name || 'System';

                        return (
                            <tr key={l._id} className="border-b border-ink-100 hover:bg-brand-teal-50/30 transition">
                                <td className="px-3 py-2 text-xs text-ink-500 whitespace-nowrap">{new Date(l.at).toLocaleString()}</td>
                                {showActor && (
                                    <td className="px-3 py-2 text-xs">
                                        {actorId ? (
                                            <Link
                                                to={`/admin/activity/actor/${actorId}`}
                                                className="font-semibold text-brand-teal hover:underline"
                                                title="View all activity by this person"
                                            >
                                                {actorName}
                                            </Link>
                                        ) : (
                                            <div className="font-semibold">{actorName}</div>
                                        )}
                                        <div className="text-[10px] uppercase tracking-wider text-ink-500">{l.actorRole || '—'}</div>
                                    </td>
                                )}
                                <td className="px-3 py-2">
                                    <div className="text-sm text-ink-900 leading-snug">{summary}</div>
                                    {detail && (
                                        <div className="font-mono text-[10px] text-ink-400 mt-0.5 truncate max-w-[320px]" title={detail}>
                                            {detail}
                                        </div>
                                    )}
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`ag-chip ${METHOD_CHIP[l.method] || ''}`}>{l.method}</span>
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`ag-chip ${STATUS_CHIP(l.status)}`}>{l.status}</span>
                                </td>
                                <td className="px-3 py-2 font-mono text-[10px] text-ink-500">{l.ip || '—'}</td>
                            </tr>
                        );
                    })}
                    {items.length === 0 && (
                        <tr>
                            <td colSpan={showActor ? 6 : 5} className="text-center text-ink-500 py-12">{emptyMessage}</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}
